import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import reduce from 'lodash/reduce'
import type {ProductPathsT} from '@freckle/educator-entities/ts/common/helpers/products'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'

//
// Have the API tell the browser to remove the current teacher session
// cookie. The client cannot interact with the cookie.
//
export function deleteSessionCookie(): Promise<{redirectUrl?: string}> {
  return fancyClient
    .DELETE('/2/teachers/sessions')
    .then(extractData)
    .then(({redirectUrl}) => ({redirectUrl: redirectUrl ?? undefined}))
}

//
// Check if the cookie on the client represents a valid teacher session
//
export function checkSessionCookie(): Promise<{provider: string}> {
  return fancyClient.GET('/2/teachers/sessions').then(extractData)
}

//
// Obtain a valid session cookie.
//
export async function postLogin(email: string, password: string): Promise<void> {
  await fancyClient.POST('/2/teachers/sessions', {
    body: {email, password}
  })
}

export type EditStudentsPayloadT = {
  studentsToAdd: Array<{studentId: number}>
  studentsToRemove: Array<number>
}

export function editStudentsForAssignmentCurried(
  path: ProductPathsT
): (assignmentId: number, editedStudents: EditStudentsPayloadT) => Promise<void> {
  return (assignmentId, editedStudents) =>
    editStudentsForAssignment(path, assignmentId, editedStudents)
}

async function editStudentsForAssignment(
  path: ProductPathsT,
  assignmentId: number,
  editedStudents: EditStudentsPayloadT
): Promise<void> {
  fancyClient.PATCH(
    '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}/sessions',
    {
      params: {
        path: {
          'SubjectP-Subject': path.subjectPath,
          'ProductP-AbbreviatedProduct': path.productPath,
          'SubjectProductAssignmentP-Int64': assignmentId.toString()
        }
      },
      body: editedStudents
    }
  )
}

export async function deleteSubjectProductAssignmentSessions(
  productPaths: ProductPathsT,
  assignmentIds: number[]
): Promise<void> {
  fancyClient.DELETE(
    '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions',
    {
      params: {
        path: {
          'SubjectP-Subject': productPaths.subjectPath,
          'ProductP-AbbreviatedProduct': productPaths.productPath
        },
        query: {
          assignmentIds
        }
      }
    }
  )
}

async function deleteAllMathIncompleteAssignmentSessions({
  adaptiveAssignmentIds,
  assessmentAssignmentIds,
  factPracticeAssignmentIds,
  iblAssignmentIds,
  standardAssignmentIds,
  numberFactsAssignmentIds
}: DeleteAllMathIncompleteAssignmentSessionsT): Promise<void> {
  await Promise.all([
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.MathAssessments),
      assessmentAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.MathAdaptive),
      adaptiveAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.MathFactPractice),
      factPracticeAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(Products.getPaths(Products.MathIbls), iblAssignmentIds),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.MathTargeted),
      standardAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.MathNumberFacts),
      numberFactsAssignmentIds
    )
  ])
}

type DeleteAllMathIncompleteAssignmentSessionsT = {
  adaptiveAssignmentIds: Array<number>
  assessmentAssignmentIds: Array<number>
  factPracticeAssignmentIds: Array<number>
  iblAssignmentIds: Array<number>
  standardAssignmentIds: Array<number>
  numberFactsAssignmentIds: Array<number>
}

type DeleteAllElaIncompleteAssignmentSessionsT = {
  readingAssignmentIds: Array<number>
  writingAssignmentIds: Array<number>
  assessmentAssignmentIds: Array<number>
  adaptiveSkillsAssignmentIds: Array<number>
  targetedSkillsAssignmentIds: Array<number>
  decodablesAssignmentIds: Array<number>
}

type DeleteAllSocialStudiesIncompleteAssignmentSessionsT = {
  readingAssignmentIds: Array<number>
  writingAssignmentIds: Array<number>
}

type DeleteAllScienceIncompleteAssignmentSessionsT = {
  readingAssignmentIds: Array<number>
  writingAssignmentIds: Array<number>
}

export type DeleteAllIncompleteAssignmentSessionsT = {
  math: DeleteAllMathIncompleteAssignmentSessionsT
  ela: DeleteAllElaIncompleteAssignmentSessionsT
  socialStudies: DeleteAllSocialStudiesIncompleteAssignmentSessionsT
  science: DeleteAllScienceIncompleteAssignmentSessionsT
}

export const emptyDeleteAllIncompleteAssignmentSessions: DeleteAllIncompleteAssignmentSessionsT = {
  math: {
    adaptiveAssignmentIds: [],
    assessmentAssignmentIds: [],
    factPracticeAssignmentIds: [],
    iblAssignmentIds: [],
    standardAssignmentIds: [],
    numberFactsAssignmentIds: []
  },
  ela: {
    readingAssignmentIds: [],
    writingAssignmentIds: [],
    assessmentAssignmentIds: [],
    adaptiveSkillsAssignmentIds: [],
    targetedSkillsAssignmentIds: [],
    decodablesAssignmentIds: []
  },
  science: {writingAssignmentIds: [], readingAssignmentIds: []},
  socialStudies: {writingAssignmentIds: [], readingAssignmentIds: []}
}

export function numDeleteAllIncompleteAssignmentSessions(
  deletes: DeleteAllIncompleteAssignmentSessionsT
): number {
  // This is a bit unsafe by recursively summing the length of all array properties
  // The advantage is that it will stay in sync if we add any new assignment types to DeleteAllIncompleteAssignmentSessionsT
  const sumLengthOfArrayProperties = (obj: object | object[]) => reduce(obj, getArrCount, 0)
  function getArrCount(acc: number, objValue: object | object[]): number {
    return (
      acc +
      (isArray(objValue)
        ? objValue.length
        : isObject(objValue)
          ? sumLengthOfArrayProperties(objValue)
          : 0)
    )
  }

  return sumLengthOfArrayProperties(deletes)
}

export async function deleteAllIncompleteAssignmentSessions({
  math,
  ela,
  socialStudies,
  science
}: DeleteAllIncompleteAssignmentSessionsT): Promise<void> {
  await Promise.all([
    deleteAllScienceIncompleteAssignmentSessions(science),
    deleteAllSocialStudiesIncompleteAssignmentSessions(socialStudies),
    deleteAllElaIncompleteAssignmentSessions(ela),
    deleteAllMathIncompleteAssignmentSessions(math)
  ])
}

async function deleteAllElaIncompleteAssignmentSessions({
  readingAssignmentIds,
  writingAssignmentIds,
  assessmentAssignmentIds,
  adaptiveSkillsAssignmentIds,
  targetedSkillsAssignmentIds,
  decodablesAssignmentIds
}: DeleteAllElaIncompleteAssignmentSessionsT): Promise<void> {
  await Promise.all([
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaArticlesReading),
      readingAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaArticlesWriting),
      writingAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaAssessments),
      assessmentAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaAdaptiveSkillsPractice),
      adaptiveSkillsAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaTargetedSkillsPractice),
      targetedSkillsAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ElaDecodables),
      decodablesAssignmentIds
    )
  ])
}

function deleteAllSocialStudiesIncompleteAssignmentSessions({
  readingAssignmentIds,
  writingAssignmentIds
}: DeleteAllSocialStudiesIncompleteAssignmentSessionsT): Promise<Array<void>> {
  return Promise.all([
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.SocialStudiesArticlesReading),
      readingAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.SocialStudiesArticlesWriting),
      writingAssignmentIds
    )
  ])
}

function deleteAllScienceIncompleteAssignmentSessions({
  readingAssignmentIds,
  writingAssignmentIds
}: DeleteAllScienceIncompleteAssignmentSessionsT): Promise<Array<void>> {
  return Promise.all([
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ScienceArticlesReading),
      readingAssignmentIds
    ),
    deleteSubjectProductAssignmentSessions(
      Products.getPaths(Products.ScienceArticlesWriting),
      writingAssignmentIds
    )
  ])
}
